/**
 * D4F OCCASION SELECTOR
 * - Interaktive Anlass-Auswahl
 * - Panel Switching
 * - Copy-to-Clipboard für Sprüche
 * - Toast Notifications
 */
(function () {
  const selectors = document.querySelectorAll('.d4f-occasion-selector');
  
  selectors.forEach(selector => {
    initOccasionSelector(selector);
  });

  function initOccasionSelector(selector) {
    const cards = selector.querySelectorAll('.d4f-occasion-card');
    const panels = selector.querySelectorAll('.d4f-occasion-panel');
    const copyButtons = selector.querySelectorAll('.d4f-suggestion-card__copy');
    const toast = document.getElementById('d4f-copy-toast');

    if (!cards.length || !panels.length) return;

    // ===== SETUP SVG ICONS WITH MASK =====
    setupIconMasks(selector);

    // ===== OCCASION CARD SWITCHING =====
    cards.forEach(card => {
      card.addEventListener('click', () => {
        const occasionId = card.dataset.occasionId;
        switchOccasion(occasionId, cards, panels);
      });

      // Keyboard Support
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const occasionId = card.dataset.occasionId;
          switchOccasion(occasionId, cards, panels);
        }
      });
    });

    // Arrow Key Navigation zwischen Cards
    cards.forEach((card, index) => {
      card.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          const nextCard = cards[index + 1] || cards[0];
          nextCard.focus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prevCard = cards[index - 1] || cards[cards.length - 1];
          prevCard.focus();
        }
      });
    });

    // ===== COPY FUNCTIONALITY =====
    copyButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.stopPropagation();
        const text = button.dataset.text;
        await copyToClipboard(text, toast);
      });
    });
  }

  /**
   * Setup SVG Icons as CSS Masks for color control
   */
  function setupIconMasks(selector) {
    const iconWrappers = selector.querySelectorAll('.d4f-occasion-card__icon');
    
    iconWrappers.forEach(wrapper => {
      const img = wrapper.querySelector('.d4f-occasion-card__icon-img');
      
      if (!img || !img.src) return;
      
      // Check if image is PNG or other non-SVG format
      const isPNG = img.src.toLowerCase().match(/\.(png|jpg|jpeg|gif|webp)$/);
      
      if (isPNG) {
        // For PNG/JPG: Add class and don't apply mask
        wrapper.classList.add('d4f-occasion-card__icon--png');
        return;
      }
      
      // For SVG: Set the mask-image
      wrapper.style.setProperty('--icon-mask', `url(${img.src})`);
      wrapper.style.webkitMaskImage = `url(${img.src})`;
      wrapper.style.maskImage = `url(${img.src})`;
      
      // Calculate and set hover color (lighter version)
      const iconColor = getComputedStyle(wrapper).getPropertyValue('--icon-color').trim();
      if (iconColor) {
        const hoverColor = lightenColor(iconColor, 15);
        wrapper.style.setProperty('--icon-color-hover', hoverColor);
      }
    });
  }

  /**
   * Lighten a hex color by a percentage
   */
  function lightenColor(color, percent) {
    // Remove # if present
    color = color.replace('#', '');
    
    // Parse RGB
    const num = parseInt(color, 16);
    let r = (num >> 16) + Math.round(2.55 * percent);
    let g = ((num >> 8) & 0x00FF) + Math.round(2.55 * percent);
    let b = (num & 0x0000FF) + Math.round(2.55 * percent);
    
    // Ensure values are within 0-255
    r = Math.min(255, Math.max(0, r));
    g = Math.min(255, Math.max(0, g));
    b = Math.min(255, Math.max(0, b));
    
    // Convert back to hex
    return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
  }

  function switchOccasion(occasionId, cards, panels) {
    // Update Active Card
    cards.forEach(card => {
      if (card.dataset.occasionId === occasionId) {
        card.classList.add('d4f-occasion-card--active');
        card.setAttribute('aria-selected', 'true');
      } else {
        card.classList.remove('d4f-occasion-card--active');
        card.setAttribute('aria-selected', 'false');
      }
    });

    // Update Active Panel
    panels.forEach(panel => {
      if (panel.dataset.panelId === occasionId) {
        panel.classList.add('d4f-occasion-panel--active');
        panel.setAttribute('aria-hidden', 'false');
        
        // Scroll to panel smoothly
        setTimeout(() => {
          const panelTop = panel.getBoundingClientRect().top + window.scrollY;
          const offset = 100; // Offset für bessere Sichtbarkeit
          window.scrollTo({
            top: panelTop - offset,
            behavior: 'smooth'
          });
        }, 100);
      } else {
        panel.classList.remove('d4f-occasion-panel--active');
        panel.setAttribute('aria-hidden', 'true');
      }
    });

    // Announce für Screen Reader
    announceOccasionChange(occasionId, panels);
  }

  function announceOccasionChange(occasionId, panels) {
    const activePanel = Array.from(panels).find(
      panel => panel.dataset.panelId === occasionId
    );

    if (!activePanel) return;

    const title = activePanel.querySelector('.d4f-occasion-panel__title');
    const suggestionCount = activePanel.querySelectorAll('.d4f-suggestion-card').length;

    const announcement = title
      ? `${title.textContent}. ${suggestionCount} Vorschläge verfügbar.`
      : `${suggestionCount} Vorschläge verfügbar.`;

    // Create or update live region
    let liveRegion = document.getElementById('d4f-occasion-announcement');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'd4f-occasion-announcement';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = announcement;
  }

  async function copyToClipboard(text, toast) {
    try {
      // Modern Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        showToast(toast, 'Text kopiert!');
      } else {
        // Fallback für ältere Browser
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        
        if (successful) {
          showToast(toast, 'Text kopiert!');
        } else {
          showToast(toast, 'Kopieren fehlgeschlagen');
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
      showToast(toast, 'Kopieren fehlgeschlagen');
    }
  }

  function showToast(toast, message = 'Text kopiert!') {
    if (!toast) return;

    // Update message if different
    const messageSpan = toast.querySelector('span');
    if (messageSpan) {
      messageSpan.textContent = message;
    }

    // Show toast
    toast.classList.add('d4f-toast--show');

    // Hide after 2.5 seconds
    setTimeout(() => {
      toast.classList.remove('d4f-toast--show');
    }, 2500);
  }

  // ===== ACCESSIBILITY: VISUALLY HIDDEN HELPER =====
  if (!document.querySelector('.visually-hidden-styles')) {
    const style = document.createElement('style');
    style.className = 'visually-hidden-styles';
    style.textContent = `
      .visually-hidden {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }
    `;
    document.head.appendChild(style);
  }

  // ===== PERFORMANCE: INTERSECTION OBSERVER =====
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    // Observe suggestion cards for lazy animation
    const suggestionCards = document.querySelectorAll('.d4f-suggestion-card');
    suggestionCards.forEach((card, index) => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      card.style.transition = `opacity 0.5s ease ${index * 0.05}s, transform 0.5s ease ${index * 0.05}s`;
      observer.observe(card);
    });
  }

  // ===== ANALYTICS TRACKING (Optional) =====
  function trackOccasionSelection(occasionId) {
    // Google Analytics
    if (window.gtag) {
      window.gtag('event', 'occasion_select', {
        occasion_id: occasionId
      });
    }

    // Facebook Pixel
    if (window.fbq) {
      window.fbq('trackCustom', 'OccasionSelect', {
        occasion_id: occasionId
      });
    }

    // Custom tracking
    console.log('[D4F] Occasion selected:', occasionId);
  }

  // Add tracking to switch function (optional)
  const originalSwitchOccasion = switchOccasion;
  switchOccasion = function(occasionId, cards, panels) {
    trackOccasionSelection(occasionId);
    return originalSwitchOccasion(occasionId, cards, panels);
  };

  // ===== PRELOAD FIRST PANEL =====
  // Ensure first panel is visible on load
  selectors.forEach(selector => {
    const firstCard = selector.querySelector('.d4f-occasion-card');
    const firstPanel = selector.querySelector('.d4f-occasion-panel');
    
    if (firstCard && firstPanel) {
      firstCard.setAttribute('aria-selected', 'true');
      firstPanel.setAttribute('aria-hidden', 'false');
    }
  });

  // ===== RESPONSIVE: ADJUST GRID ON RESIZE =====
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      // Recalculate grid layouts if needed
      selectors.forEach(selector => {
        const grid = selector.querySelector('.d4f-occasion-panel__grid');
        if (grid) {
          // Force reflow
          grid.style.display = 'none';
          grid.offsetHeight; // Trigger reflow
          grid.style.display = '';
        }
      });
    }, 250);
  });
})();